define('personalization/personalization-wrapper',['adlhelpers', 'basil', 'domhelpers', 'edap'], function(ADL, Basil, domHelpers, edap) {
    'use strict';

    function Personalization() {
        var checkoutKey = 'lastCheckouts',
            searchKey = 'lastSearches',
            pdpKey = 'lastPDPs',
            maxCheckoutLogs = 3,
            maxSearchLogs = 10,
            maxPDPLogs = 10;

        /**
         *  Options for configuring Basil
         */
        this.basilOpts = {
            namespace: 'edap',
            storages: ['local']
        };


        /**
         *  Cached Basil instance
         */
        this.basil = new Basil(this.basilOpts);


        /**
         *  Gets the data associated with the key in LocalStorage. If there is an error
         *  getting the data then we will clear the key from LocalStorage in an effort to
         *  prevent future read issues for that key.
         *
         *  @param {String} [key] - The key the data is associated with
         *  @returns {Object} - The data object from LocalStorage associated with the key, or null
         *                      if the key is missing or an error occurs in decoding the data
         */
        this.getDataByKey = function(key) {
            var data = null;

            try {
                data = this.basil.get(key);
            } catch (e) {
                edap.error(e);
                this.basil.remove(key);
            }

            return data;
        };


        /**
         *  Sets a key-value pair in LocalStorage
         *
         *  @param {String} [key] - The key to store the data
         *  @param {Object} [value] - The data Object that will be JSON.stringify()'d
         *  @returns {Boolean} - true if the data was set, false if it was not
         */
        this.setData = function(key, value) {
            try {
                return this.basil.set(key, value);
            } catch (e) {
                edap.error(e);
            }

            return false;
        };


        /**
         *  Processes a new Item to be inserted into storage. It does the following:
         *   - Pushes the new Item into the existing data array, or creates a new data array
         *   - Sorts the array by nearest to farthest timestamps
         *   - If there are > maxEntries then it will remove entries from oldest to newest until it is within maxEntries
         *
         *  @param {String} [storageKey] - Key to use to get the data from LocalStorage
         *  @param {Object} [newItem] - New object to insert into LocalStorage
         *  @param {Integer} [maxEntries] - Maximum number of items in the storageKey array
         *  @returns {Boolean} - true if the data was set, false if it was not
         */
        this.setSortedDataItem = function(storageKey, newItem, maxEntries) {
            var data = this.getDataByKey(storageKey);

            // If we have no data then we should create a new array
            if (data === null) {
                data = [];
            }

            // Add the object to the list
            data.push(newItem);

            // We will sort the checkouts by largest to smallest timestamp
            data = data.sort(function(a, b) {
                return b.timestamp - a.timestamp;
            });

            // If we are over our max then trim out all but the most recent maxEntries
            // (The new item just pushed should be the first item).
            if (data.length >= maxEntries) {
                data = data.splice(0, maxEntries);
            }

            // Set the new object in LocalStorage
            return this.setData(storageKey, data);
        };


        /**
         *  Store a checkout session info in LocalStorage.
         *
         *  @param {Object} [adlData] - The ADL object to use to build the checkout session info to store
         *  @returns {Boolean} - true if the data was set, false if it was not
         */
        this.setCheckout = function(adlData) {
            var adl = new ADL(adlData),
                checkoutData = this.getDataByKey(checkoutKey),
                listingid = adl.get('listingid'),
                newCheckoutItem = {},
                i,
                len;

            // If we have existing data then we need to remove any old checkouts for this listing id
            if (checkoutData instanceof Array) {
                // Now let's check to see if this listingid is already present and remove that from the existing list
                for (i = 0, len = checkoutData.length; i < len; i++) {
                    if (listingid === checkoutData[i].listingid) {
                        checkoutData.splice(i, 1);
                        this.setData(checkoutKey, checkoutData);
                        break;
                    }
                }
            }

            // Build the checkout session info object
            newCheckoutItem = {
                listingid: adl.get('listingid'),
                url: adl.get('href'),
                startdate: adl.get('eventstartdate'),
                enddate: adl.get('eventenddate'),
                adults: adl.get('eventnumadults'),
                children: adl.get('eventnumchildren'),
                pets: adl.get('eventpets'),
                triptotal: adl.get('triptotal'),
                timestamp: domHelpers.getMsFromEpoch()
            };

            return this.setSortedDataItem(checkoutKey, newCheckoutItem, maxCheckoutLogs);
        };


        /**
         *  Store a search session info in LocalStorage.
         *
         *  @param {Object} [adlData] - The ADL object to use to build the search session info to store
         *  @returns {Boolean} - true if the data was set, false if it was not
         */
        this.setSearch = function(adlData) {
            var adl = new ADL(adlData),
                defaultValue = null,
                newSearchItem = {};

            // Build the search session info object
            newSearchItem = {
                searchterm: adl.get('searchterm'),
                startdate: (adl.isSet('startdate') ? adl.get('startdate') : defaultValue),
                enddate: (adl.isSet('enddate') ? adl.get('enddate') : defaultValue),
                guests: (adl.isSet('minsleeps') ? adl.get('minsleeps') : defaultValue),
                url: domHelpers.getHref(),
                timestamp: domHelpers.getMsFromEpoch()
            };

            return this.setSortedDataItem(searchKey, newSearchItem, maxSearchLogs);
        };


        /**
         *  Store a PDP pageview info in LocalStorage.
         *
         *  @param {Object} [adlData] - The ADL object to use to build the PDP pageview info to store
         *  @returns {Boolean} - true if the data was set, false if it was not
         */
        this.setPDP = function(adlData) {
            var adl = new ADL(adlData),
                defaultValue = null,
                newPDPItem = {};

            // Build the PDP pageview info object
            newPDPItem = {
                listingid: adl.get('listingid'),
                startdate: (adl.isSet('startdate') ? adl.get('startdate') : defaultValue),
                enddate: (adl.isSet('enddate') ? adl.get('enddate') : defaultValue),
                url: domHelpers.getHref(),
                timestamp: domHelpers.getMsFromEpoch()
            };

            return this.setSortedDataItem(pdpKey, newPDPItem, maxPDPLogs);
        };
    }

    return new Personalization();
});

